import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, History, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";

/**
 * Right-side drawer listing snapshots of a CMS page. Restoring pushes the
 * current state to history first, so a restore is itself undoable.
 */
export default function VersionHistoryDrawer({ slug, open, onClose, onRestored }) {
  const [versions, setVersions] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open || !slug) return;
    setVersions(null);
    api.get(`/admin/cms/pages/${slug}/versions`)
      .then(({ data }) => setVersions(data))
      .catch(() => setVersions([]));
  }, [slug, open]);

  const restore = async (v) => {
    if (!confirm(`Roll back "${slug}" to the version saved on ${formatDateTime(v.created_at)}?`)) return;
    setRestoringId(v.id);
    try {
      await api.post(`/admin/cms/pages/${slug}/versions/${v.id}/restore`);
      toast.success("Page rolled back.");
      onRestored && onRestored();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restore failed");
    } finally { setRestoringId(null); }
  };

  const openPreview = async (v) => {
    try {
      const { data } = await api.get(`/admin/cms/pages/${slug}/versions/${v.id}`);
      setPreview(data);
    } catch (err) { toast.error("Failed to load version"); }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      <aside
        data-testid="cms-version-drawer"
        className="fixed right-0 top-0 bottom-0 w-full sm:w-[420px] bg-white z-50 flex flex-col shadow-2xl"
      >
        <header className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
          <div className="flex items-center gap-2">
            <History size={18} className="text-[#538135]" />
            <div>
              <h3 className="font-display font-semibold text-gray-900">Version history</h3>
              <p className="text-xs text-gray-500 font-mono">/{slug}</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="cms-version-drawer-close"
            className="p-1 text-gray-500 hover:text-gray-900">
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4">
          {!versions && <div className="text-sm text-gray-500 p-4">Loading…</div>}
          {versions && versions.length === 0 && (
            <div className="text-center py-14 text-sm text-gray-500">
              No previous versions yet. Save this page to create the first snapshot.
            </div>
          )}
          {versions && versions.length > 0 && (
            <ul className="space-y-2">
              {versions.map((v, i) => (
                <li key={v.id}
                  data-testid={`cms-version-${v.id}`}
                  className="border border-[#E5E7EB] rounded-lg p-3 hover:border-[#538135]/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className={`text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded ${
                          v.action === "create" ? "bg-[#EAF4E3] text-[#09582D]"
                          : v.action === "delete" ? "bg-red-50 text-red-700"
                          : "bg-gray-100 text-gray-700"
                        }`}>{v.action}</span>
                        {i === 0 && <span className="text-[10px] text-gray-500">most recent</span>}
                      </div>
                      <div className="text-sm font-medium text-gray-900 truncate">{v.title}</div>
                      <div className="text-xs text-gray-500 truncate mt-0.5">
                        {v.actor_email || "system"} · {formatDateTime(v.created_at)}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => openPreview(v)}
                      data-testid={`cms-version-${v.id}-preview`}
                      className="flex-1 text-xs px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Preview
                    </button>
                    <button
                      onClick={() => restore(v)}
                      disabled={restoringId === v.id}
                      data-testid={`cms-version-${v.id}-restore`}
                      className="flex-1 text-xs px-3 py-1.5 bg-[#538135] text-white hover:bg-[#09582D] rounded-md font-semibold inline-flex items-center justify-center gap-1 disabled:opacity-60"
                    >
                      <RotateCcw size={11} /> {restoringId === v.id ? "Restoring…" : "Restore"}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>

      {preview && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}>
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            <header className="p-4 border-b border-[#E5E7EB] flex items-center justify-between">
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider">Version preview</div>
                <div className="font-display font-semibold text-gray-900">{preview.title}</div>
                <div className="text-xs text-gray-500 font-mono mt-0.5">
                  {formatDateTime(preview.created_at)} · {preview.actor_email}
                </div>
              </div>
              <button onClick={() => setPreview(null)}
                className="p-1 text-gray-500 hover:text-gray-900"><X size={16} /></button>
            </header>
            <div className="flex-1 overflow-y-auto p-4 bg-[#F8FAF7]">
              <pre className="text-[11px] font-mono text-gray-700 whitespace-pre-wrap break-all">
                {JSON.stringify({
                  seo_title: preview.seo_title,
                  seo_description: preview.seo_description,
                  published: preview.published,
                  sections: preview.sections,
                }, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
